import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { iconsBranding } from 'constants/branding';
import { BET_LIST_URL } from 'constants/locations';
import useDevice from 'hooks/useDevice';
import { getIsAVActiveProfitLossTableForCurrentBet } from 'redux/modules/asianViewCurrentBets/selectors';
import { getLoggedInStatusState } from 'redux/modules/auth/selectors';
import {
  closeProfitLossTablePopUp,
  fetchProfitLossMarketNames,
  fetchProfitLossTableData
} from 'redux/modules/profitLossTablePopUp';
import { getProfitLossTablePopUpEventId } from 'redux/modules/profitLossTablePopUp/selectors';

import styles from './ProfitLossTablePopUpButton.module.scss';

type ProfitLossTablePopUpButtonProps = {
  className?: string;
  eventId: string;
  isActive?: boolean;
  isDisabled?: boolean;
  marketId?: string;
  iconClassName?: string;
  isBrandingStyles?: boolean;
  isAsianView?: boolean;
};

const ProfitLossTablePopUpButton = ({
  className = '',
  eventId,
  marketId,
  isActive = false,
  isDisabled = false,
  iconClassName = '',
  isBrandingStyles = true,
  isAsianView = true
}: ProfitLossTablePopUpButtonProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isBetListPage = pathname.includes(BET_LIST_URL);

  const isLoggedIn = useSelector(getLoggedInStatusState);
  const isActivePLTable = useSelector(
    getIsAVActiveProfitLossTableForCurrentBet(!isLoggedIn || isActive ? undefined : eventId)
  );
  const openedEventId = useSelector(getProfitLossTablePopUpEventId);

  const { isMobile, isDesktop } = useDevice();

  if (!isLoggedIn) {
    return null;
  }

  const handleFetchProfitLossTableData = () => {
    dispatch(closeProfitLossTablePopUp());

    // setTimeout is required here because previous pop up should be closed and new one should be opened, but there is
    // not enough time to close previous pop up (set eventId to null) and open new one (set new eventId)
    setTimeout(() => {
      if (marketId) {
        dispatch(fetchProfitLossTableData({ eventId, marketId, isAsianView }));
        dispatch(fetchProfitLossMarketNames({ eventId, isAsianView }));
      }
    }, 0);
  };

  return (
    <button
      {...(isMobile ? {} : { 'data-tooltip-id': 'tooltip', 'data-tooltip-html': t('betslip.tooltip.plTable') })}
      onClick={handleFetchProfitLossTableData}
      className={classNames(styles.buttonWrap, { [styles.buttonWrap__big]: isMobile && isBetListPage })}
      disabled={isDisabled}
    >
      <span
        className={classNames(styles.plIcon, className, {
          'fa2 fa2-pl-bg-16': isDesktop,
          [styles.plIcon__desktop]: isDesktop,
          [styles.plIcon__active]: isActive || isActivePLTable,
          [styles.plIcon__opened]: !(isActive || isActivePLTable) && openedEventId === eventId,
          [styles.plIcon__active__opened]: (isActive || isActivePLTable) && openedEventId === eventId,
          [styles.plIcon__mobile]: isMobile && !isBetListPage,
          [styles.plIcon__big]: isMobile && isBetListPage,
          [iconsBranding.PL_ICON]: isBrandingStyles,
          [iconsBranding.ACTIVE]: isBrandingStyles && (isActive || isActivePLTable),
          [iconsBranding.SELECTED]: isBrandingStyles && openedEventId === eventId
        })}
      >
        <i
          className={classNames('fa2', styles.borderIcon, {
            'fa2-pl-border-16': isDesktop,
            'fa2-pl-border-20': isMobile,
            [iconsBranding.PL_BORDER_ICON]: isBrandingStyles
          })}
        />
        <i
          className={classNames('fa2', styles.signIcon, iconClassName, {
            'fa2-pl-sign-16': isDesktop,
            'fa2-pl-sign-20': isMobile,
            [iconsBranding.PL_SIGN_ICON]: isBrandingStyles
          })}
        />
      </span>
    </button>
  );
};

export default ProfitLossTablePopUpButton;
